import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaCalendarAlt, FaBars, FaHome, FaUser } from 'react-icons/fa';
import { auth } from '../firebase';
import './Navbar.css';

const BASE_URL = 'https://api.rside.app';

function Navbar({ title = 'Dashboard' }) {
  const navigate = useNavigate();
  const [initials, setInitials] = useState('');
  const [fullName, setFullName] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState({ properties: [], tenants: [] });
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const searchWrapRef = useRef(null);
  const debounceRef = useRef(null);
  const cachedDataRef = useRef(null);
  const userEmailRef = useRef('');
  const fullNameRef = useRef('');

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    userEmailRef.current = user.email;
    fetch(`${BASE_URL}/PropertyManagerName?email=${encodeURIComponent(user.email)}`)
      .then(res => res.json())
      .then(data => {
        if (data.Full_Name) {
          fullNameRef.current = data.Full_Name;
          const parts = data.Full_Name.trim().split(/\s+/);
          const first = parts[0]?.[0] || '';
          const last = parts[parts.length - 1]?.[0] || '';
          setInitials((first + last).toUpperCase());
          setFullName(data.Full_Name);
        }
      })
      .catch(err => console.error('Failed to fetch PM name:', err));
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchAllData = useCallback(async () => {
    if (cachedDataRef.current) return cachedDataRef.current;
    const email = userEmailRef.current || auth.currentUser?.email || '';
    const fn = fullNameRef.current;
    if (!email) return null;

    // Fetch properties — must have full_name
    let properties = [];
    if (fn) {
      try {
        const res = await fetch(
          `${BASE_URL}/GetAllProperties?full_name=${encodeURIComponent(fn)}&Email=${encodeURIComponent(email)}`
        );
        if (res.ok) {
          const data = await res.json();
          const seen = new Map();
          for (const p of data.Properties || []) {
            const id = p.Property_ID;
            if (!seen.has(id)) {
              seen.set(id, {
                id,
                name: p.Property_Name || '',
                address: p.Street_Address || '',
                city: p.City || '',
                status: p.Status || '',
                units: [],
              });
            }
            seen.get(id).units.push({ doorId: p.Door_id, status: p.Status, unitNumber: p.Unit_Number });
          }
          properties = Array.from(seen.values());
        }
      } catch (e) {
        console.error('Property fetch error:', e);
      }
    }

    // Extract tenants from occupied doors (limit to 15 doors to avoid too many requests)
    const occupiedDoors = [];
    for (const prop of properties) {
      for (const unit of prop.units) {
        if (unit.status === 'Occupied' && unit.doorId) {
          occupiedDoors.push({ doorId: unit.doorId, propertyId: prop.id, propertyName: prop.name, propertyAddress: prop.address });
          if (occupiedDoors.length >= 15) break;
        }
      }
      if (occupiedDoors.length >= 15) break;
    }

    const tenants = [];
    if (occupiedDoors.length > 0) {
      const results = await Promise.allSettled(
        occupiedDoors.map(d =>
          fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${d.doorId}`)
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (!data) return null;
              const list = data.tenants || (Array.isArray(data) ? data : []);
              const t = list[0] || data.tenant;
              if (!t) return null;
              return {
                tenant_id: t.tenant_id || t.Tenant_ID,
                name: t.Full_Name || t.full_name || '',
                propertyId: d.propertyId,
                propertyName: d.propertyName,
                propertyAddress: d.propertyAddress,
              };
            })
        )
      );
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value?.name) tenants.push(r.value);
      }
    }

    cachedDataRef.current = { properties, tenants };
    return cachedDataRef.current;
  }, []);

  const runSearch = useCallback(async (q) => {
    const trimmed = q.trim();
    if (!trimmed) {
      setSuggestions({ properties: [], tenants: [] });
      setShowDropdown(false);
      return;
    }

    // If full name not loaded yet, wait for it
    if (!fullNameRef.current) {
      setSuggestions({ properties: [], tenants: [] });
      setShowDropdown(false);
      return;
    }

    setSearchLoading(true);
    try {
      const data = await fetchAllData();
      if (!data) { setSearchLoading(false); return; }

      const lower = trimmed.toLowerCase();

      const matchedProps = data.properties
        .filter(p => {
          return (p.name || '').toLowerCase().includes(lower) ||
                 (p.address || '').toLowerCase().includes(lower) ||
                 (p.city || '').toLowerCase().includes(lower);
        })
        .slice(0, 5);

      const matchedTenants = data.tenants
        .filter(t => (t.name || '').toLowerCase().includes(lower))
        .slice(0, 5);

      setSuggestions({ properties: matchedProps, tenants: matchedTenants });
      setShowDropdown(matchedProps.length > 0 || matchedTenants.length > 0);
    } catch (e) {
      console.error('Search error:', e);
    }
    setSearchLoading(false);
  }, [fetchAllData]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    if (!val.trim()) {
      setSuggestions({ properties: [], tenants: [] });
      setShowDropdown(false);
      setSearchLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(val), 300);
  };

  const handlePropertyClick = (prop) => {
    navigate(`/properties/${prop.id}`);
    setShowDropdown(false);
    setSearchQuery('');
  };

  const handleTenantClick = (tenant) => {
    navigate(`/properties/${tenant.propertyId}`);
    setShowDropdown(false);
    setSearchQuery('');
  };

  const totalResults = suggestions.properties.length + suggestions.tenants.length;

  return (
    <div className="navbar">
      <button
        className="navbar-hamburger"
        onClick={() => document.body.classList.toggle('sidebar-mobile-open')}
        aria-label="Toggle menu"
      >
        <FaBars />
      </button>
      <div className="navbar-left">
        <h1 className="navbar-title">{title}</h1>
        <div className="navbar-date-row">
          <FaCalendarAlt className="navbar-date-icon" />
          <span className="navbar-date-text">{dateStr}</span>
        </div>
      </div>

      <div className="navbar-center">
        <div className="navbar-search-wrap" ref={searchWrapRef}>
          <FaSearch className="navbar-search-icon" />
          <input
            type="text"
            className="navbar-search-input"
            placeholder="Search properties, tenants..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => { if (totalResults > 0) setShowDropdown(true); }}
            autoComplete="off"
          />
          {showDropdown && (
            <div className="navbar-search-dropdown">
              {searchLoading && <div className="navbar-search-loading">Searching...</div>}

              {!searchLoading && totalResults === 0 && (
                <div className="navbar-search-empty">No results found</div>
              )}

              {!searchLoading && suggestions.properties.length > 0 && (
                <div className="navbar-search-group">
                  <div className="navbar-search-group-label">
                    <FaHome className="navbar-search-group-icon" /> Properties
                  </div>
                  {suggestions.properties.map((prop) => (
                    <div
                      key={prop.id}
                      className="navbar-search-item"
                      onMouseDown={() => handlePropertyClick(prop)}
                    >
                      <div className="navbar-search-item-icon">
                        <FaHome />
                      </div>
                      <div className="navbar-search-item-text">
                        <span className="navbar-search-item-main">{prop.name || prop.address}</span>
                        {prop.address && prop.name && (
                          <span className="navbar-search-item-sub">{prop.address}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!searchLoading && suggestions.tenants.length > 0 && (
                <div className="navbar-search-group">
                  <div className="navbar-search-group-label">
                    <FaUser className="navbar-search-group-icon" /> Tenants
                  </div>
                  {suggestions.tenants.map((tenant, i) => (
                    <div
                      key={tenant.tenant_id || i}
                      className="navbar-search-item"
                      onMouseDown={() => handleTenantClick(tenant)}
                    >
                      <div className="navbar-search-item-icon tenant-icon">
                        <FaUser />
                      </div>
                      <div className="navbar-search-item-text">
                        <span className="navbar-search-item-main">{tenant.name}</span>
                        <span className="navbar-search-item-sub">{tenant.propertyName || tenant.propertyAddress}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="navbar-right">
        {fullName && (
          <div className="navbar-user-info">
            <span className="navbar-user-name">{fullName}</span>
            <span className="navbar-user-role">Property Manager</span>
          </div>
        )}
        {initials && (
          <div className="navbar-avatar">{initials}</div>
        )}
      </div>
    </div>
  );
}

export default Navbar;
